import styled from 'styled-components'

import {
  useGetStoryCardMediaQuery,
  useGetStoryPhotosLazyQuery,
} from '../../../hooks/useQueries'

import HeartOnIcon from '../../icons/BucketlistHeartOn'
import HeartOffIcon from '../../icons/BucketlistHeartOff'
import { normalizeKeys } from '../../utils/propsUtils'

import LinkOrA, { LinkOrAWrapper } from '../shared/LinkOrA'
import CardSlider from '../shared/CardSlider'
import CardTag from '../shared/CardTag'
import ListingDetails from '../shared/ListingDetails'
import DiscountBanner from '../shared/DiscountBanner'
import AvailableDates from '../shared/AvailableDates'
import { MediaWrapperOrientated } from '../shared/MediaWrapper'
import { CardWrapper, Content, BucketListButton } from '../shared/styled'

import StorytellerCreditLite from './StorytellerCreditLite'
import composePhotos from './composePhotos'
import {
  CamelCaseUser,
  CamelCaseListing,
  CamelCaseStory,
  SnakeCaseListing,
  SnakeCaseStory,
} from '../types'

// Keep in sync with photos(first:) in ../gql/getStoryCardMedia.gql
const STORY_CARD_PHOTO_PREVIEW_COUNT = 3

export const StoryCardWrapper = styled(CardWrapper)`
  white-space: normal;
`

// Bounded short of the heart so a long attribution truncates, not collides.
const Overlay = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  right: 56px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;

  ${LinkOrAWrapper} {
    min-width: 0;

    a {
      display: block;
    }
  }
`

type StoryCardProps = {
  story: CamelCaseStory | SnakeCaseStory
  listing: CamelCaseListing | SnakeCaseListing
  onSelectEscapeClick?: () => any
  isInEscape?: boolean
  asLink?: boolean
  sameTab?: boolean
  portrait?: boolean
  compactCard?: boolean
  /** Optionally pass in the photo to show as first/cover on the card */
  coverPhoto?: Pick<GQL_PhotoImage, 'medium' | 'large'>
  availableDates?: { startDate: string; endDate: string; total: number }[]
  showDriveTime?: boolean
}

export const StoryCardComponent = ({
  story,
  listing,
  isInEscape = false,
  onSelectEscapeClick = undefined,
  asLink = false,
  sameTab = false,
  portrait = true,
  compactCard,
  coverPhoto,
  availableDates,
  showDriveTime = false,
}: StoryCardProps) => {
  const normalizedUser = normalizeKeys(story.user) as CamelCaseUser
  const cardTag =
    (story as CamelCaseStory).cardTag ?? (story as SnakeCaseStory).card_tag

  const {
    pricing,
    discount,
    price,
    pricingSuffix,
    nicePricingSuffix,
    reviewScore,
    instantBookEnabled,
    driveTimeMinutes,
    accessByBoat,
    subcategory,
  } = normalizeKeys(listing) as CamelCaseListing

  const { data: mediaData, loading: mediaLoading } = useGetStoryCardMediaQuery({
    variables: { storyId: story.id.toString() },
    context: { batching: true },
  })

  const [loadFullPhotos, { data: fullPhotosData }] =
    useGetStoryPhotosLazyQuery()

  const eagerPhotos = mediaData?.story?.photos
  const mightHaveMorePhotos =
    eagerPhotos?.length === STORY_CARD_PHOTO_PREVIEW_COUNT

  const handleMediaInteraction = () => {
    if (mightHaveMorePhotos) {
      loadFullPhotos({ variables: { storyId: story.id.toString() } })
    }
  }

  const photos = composePhotos({
    isPortrait: portrait,
    featuredListingImage: mediaData?.story?.featuredListingImage,
    regularPhotos: fullPhotosData?.story?.photos ?? mediaData?.story?.photos,
    portraitCoverImage: mediaData?.story?.portraitCoverImage,
    promoImage: mediaData?.story?.promoImage,
    firstImage: coverPhoto && { image: coverPhoto },
  })

  const prefixCount = composePhotos({
    isPortrait: portrait,
    featuredListingImage: mediaData?.story?.featuredListingImage,
    regularPhotos: [],
    portraitCoverImage: mediaData?.story?.portraitCoverImage,
    promoImage: mediaData?.story?.promoImage,
    firstImage: coverPhoto && { image: coverPhoto },
  }).length

  const reservedPhotoSlots = prefixCount + (mediaData?.story?.photosCount ?? 0)

  const videos = mediaData?.story?.videos

  const avatarUrl =
    typeof story.user.avatar === 'string'
      ? story.user.avatar
      : story.user.avatar.thumbLg

  const storytellerName = normalizedUser.firstName

  return (
    <StoryCardWrapper>
      <MediaWrapperOrientated
        portrait={portrait}
        onPointerEnter={handleMediaInteraction}
        onTouchStart={handleMediaInteraction}
      >
        {photos && !mediaLoading && (
          <CardSlider
            title={story.title}
            photos={photos}
            videos={videos}
            url={story.url}
            asLink={asLink}
            portrait={portrait}
            reservedPhotoSlots={reservedPhotoSlots}
          />
        )}
        <BucketListButton
          onClick={onSelectEscapeClick}
          isActive={isInEscape}
          aria-label="Add to escape"
        >
          <HeartOnIcon />
          <HeartOffIcon />
        </BucketListButton>
      </MediaWrapperOrientated>

      <DiscountBanner discount={discount} />

      <Content compact={compactCard}>
        <ListingDetails
          title={listing.title}
          capacity={listing.capacity}
          locationName={listing.location?.name}
          city={listing.city}
          reviewScore={reviewScore}
          subcategory={subcategory}
          driveTimeMinutes={driveTimeMinutes}
          accessByBoat={accessByBoat}
          showDriveTime={showDriveTime}
          price={price}
          pricing={pricing}
          pricingSuffix={pricingSuffix}
          nicePricingSuffix={nicePricingSuffix}
          instantBookEnabled={instantBookEnabled}
          compact={compactCard}
          linkOptions={{
            url: story.url,
            asLink,
            sameTab,
          }}
        />
      </Content>

      {availableDates?.length > 0 && (
        <div style={{ marginTop: '-24px', marginBottom: '32px' }}>
          <AvailableDates
            availableDates={availableDates}
            linkOptions={{
              url: story.url,
              asLink,
              sameTab,
            }}
            thumbnail={
              coverPhoto?.medium || mediaData?.story?.promoImage?.medium
            }
            listingTitle={listing.title}
            capacity={listing.capacity}
            locationName={listing.location?.name}
          />
        </div>
      )}

      {/* NB: this element has position "absolute". It needs to be defined after preceding content, otherwise it
      will not be visible without z-index adjustment. In this case the potential z-index values are constrained by
      other elements on the page. */}
      <Overlay>
        <LinkOrA asLink={asLink} to={story.url} sameTab={sameTab}>
          <StorytellerCreditLite
            storytellerName={storytellerName}
            avatarUrl={avatarUrl}
            compact={compactCard}
          />
        </LinkOrA>
        <CardTag tag={cardTag} compact={compactCard} />
      </Overlay>
    </StoryCardWrapper>
  )
}

export default StoryCardComponent
